import { useMemo } from 'react'
import { ArrowRight, Clock } from 'lucide-react'
import { CTASection } from '@/components/landing/CTASection'
import { EditorialHeader, StickySplit } from '@/components/landing/Editorial'
import { HeroReveal, ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { BadgeMarquee, DocumentFan, TONES, monogram, pickFanDocuments, type ListingTone } from '@/components/landing/listing'
import type { IndustryData } from '@/data/industries'
import { cn } from '@/lib/utils'
import { IndustryListing } from './listing'

/** One accent per industry, drawn from the brand palette. */
const INDUSTRY_TONE: Record<string, ListingTone> = {
  legal: 'blue',
  finance: 'green',
  healthcare: 'pink',
  insurance: 'purple',
  engineering: 'black',
  consulting: 'blue',
}

/**
 * Blogs-listing layout (Legal): document-fan split hero, the filterable
 * template listing, pain points with measured impact, roles, the compliance
 * badge marquee and the blue CTA band.
 */
export function BlogsListingLayout({ industry }: { industry: IndustryData }) {
  const tone = INDUSTRY_TONE[industry.slug] ?? 'blue'
  const toneSpec = TONES[tone]
  const fanDocs = useMemo(() => pickFanDocuments(industry.templates), [industry])

  return (
    <>
      {/* Hero: industry name in a rotated highlight box, H1 under it,
          right-aligned intro beneath a fan of the industry's documents */}
      <section className="pt-12 md:pt-20">
        <div className="landing-container">
          <div className="mx-auto border-b border-[var(--color-landing-gray-200)] pb-14 dark:border-[var(--color-landing-gray-800)] lg:max-w-[80%] lg:pb-20">
            <div className="grid items-end gap-12 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:gap-16">
              <HeroReveal delay={0.05}>
                <h1 className="text-[var(--color-landing-gray-900)] dark:text-white">
                  <span
                    className={cn(
                      'mb-6 inline-block -rotate-2 px-[0.22em] pb-[0.08em] font-[family-name:var(--font-landing-display)] text-[clamp(2.25rem,9vw,7rem)] uppercase leading-[1.3] tracking-[-0.02em]',
                      tone === 'black' && 'dark:!bg-white dark:!text-[#0a0a0a]'
                    )}
                    style={{ backgroundColor: toneSpec.bg, color: toneSpec.fg }}
                  >
                    {industry.shortName}
                  </span>
                  <span className="landing-text-display-xl block max-w-[22ch]">{industry.headline}</span>
                </h1>
              </HeroReveal>

              <div className="flex flex-col gap-10 lg:items-end">
                <DocumentFan
                  docs={fanDocs}
                  tone={tone}
                  className="pt-6 text-[clamp(7px,1.9vw,12px)] md:text-[12px] lg:pr-4 lg:text-[clamp(8px,0.68vw,12px)]"
                />
                <HeroReveal delay={0.2}>
                  <p className="landing-text-body-lg max-w-[40ch] text-pretty text-[var(--color-landing-gray-900)]/65 dark:text-white/65 lg:text-right">
                    {industry.subheadline}
                  </p>
                </HeroReveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Listing: templates, use cases and agents with filters */}
      <IndustryListing industry={industry} />

      {/* Pain points and measured impact */}
      <section className="landing-section bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
        <div className="landing-container">
          <StickySplit
            headerWidth="narrow"
            header={
              <EditorialHeader
                heading={
                  <>
                    Where the hours{' '}
                    <span className="landing-highlight">go today.</span>
                  </>
                }
                lede={`Document intensity: ${industry.documentIntensity}. Here is where that volume slows ${industry.shortName.toLowerCase()} teams down, and what changes.`}
              />
            }
          >
            <div className="flex flex-col">
              {industry.painPoints.map((point) => (
                <ScrollReveal key={point.title} direction="none" amount={0.3}>
                  <div className="grid gap-4 border-t border-[var(--color-landing-gray-300)] py-8 dark:border-[var(--color-landing-gray-700)] md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] md:gap-10">
                    <div>
                      <h3 className="landing-text-display-sm text-[var(--color-landing-gray-900)] dark:text-white">{point.title}</h3>
                      <p className="mt-3 flex items-center gap-2 text-sm text-[var(--color-landing-gray-900)]/65 dark:text-white/65">
                        <Clock aria-hidden className="h-4 w-4 shrink-0" />
                        {point.impact}
                      </p>
                    </div>
                    <p className="flex items-start gap-3 landing-text-body text-[var(--color-landing-gray-900)] dark:text-white">
                      <ArrowRight aria-hidden className="mt-1 h-5 w-5 shrink-0 text-[var(--color-landing-accent)]" />
                      {point.solution}
                    </p>
                  </div>
                </ScrollReveal>
              ))}

              <ScrollReveal direction="none" amount={0.2}>
                <div className="mt-10">
                  <table className="w-full text-left text-[0.8125rem] tabular-nums text-[var(--color-landing-gray-900)] dark:text-white sm:text-sm">
                    <caption className="mb-5 text-left font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4] tracking-[-0.02em] text-[var(--color-landing-gray-900)] dark:text-white">
                      Measured impact
                    </caption>
                    <thead>
                      <tr className="border-b border-[var(--color-landing-gray-900)] dark:border-white">
                        <th scope="col" className="py-3 pr-2 font-bold uppercase sm:pr-4 tracking-[0.04em]">Metric</th>
                        <th scope="col" className="px-2 py-3 font-bold uppercase sm:px-4 tracking-[0.04em] text-[var(--color-landing-gray-900)]/65 dark:text-white/65">Before</th>
                        <th scope="col" className="px-2 py-3 font-bold uppercase sm:px-4 tracking-[0.04em]">After</th>
                        <th scope="col" className="py-3 pl-2 text-right sm:pl-4 font-bold uppercase tracking-[0.04em] text-[var(--color-landing-accent)]">Change</th>
                      </tr>
                    </thead>
                    <tbody className="text-[var(--color-landing-gray-900)] dark:text-white">
                      {industry.roiMetrics.map((metric) => (
                        <tr key={metric.metric} className="border-b border-[var(--color-landing-gray-300)] dark:border-[var(--color-landing-gray-700)]">
                          <th scope="row" className="py-3 pr-2 sm:pr-4 font-medium">{metric.metric}</th>
                          <td className="px-2 py-3 sm:px-4 text-[var(--color-landing-gray-900)]/65 line-through dark:text-white/65">{metric.before}</td>
                          <td className="px-2 py-3 sm:px-4 font-semibold">{metric.after}</td>
                          <td className="py-3 pl-2 text-right sm:pl-4 font-bold text-[var(--color-landing-accent)]">{metric.improvement}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ScrollReveal>
            </div>
          </StickySplit>
        </div>
      </section>

      {/* Roles */}
      <section className="landing-section">
        <div className="landing-container">
          <EditorialHeader
            className="mb-16"
            heading={
              <>
                Built for{' '}
                <span className="landing-highlight landing-highlight--secondary">every role.</span>
              </>
            }
            lede={`Modelled on ${industry.sampleCompany.name}: ${industry.sampleCompany.type.toLowerCase()}, ${industry.sampleCompany.size}.`}
          />
          <StaggerContainer
            amount={0.1}
            className="grid gap-y-12 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-[var(--color-landing-gray-200)] dark:lg:divide-[var(--color-landing-gray-800)]"
          >
            {industry.personas.map((persona, i) => {
              const personaTone = TONES[(['blue', 'purple', 'pink', 'green'] as const)[i % 4]!]
              return (
                <StaggerItem key={persona.name} className="flex flex-col gap-4 sm:pr-8 lg:px-8 lg:first:pl-0 lg:last:pr-0">
                  <span
                    aria-hidden
                    className="flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold"
                    style={{ backgroundColor: personaTone.bg, color: personaTone.fg }}
                  >
                    {monogram(persona.name)}
                  </span>
                  <div>
                    <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-lg font-bold text-[var(--color-landing-gray-900)] dark:text-white">
                      {persona.role}
                    </h3>
                    <p className="text-sm text-[var(--color-landing-gray-900)]/65 dark:text-white/65">{persona.name}</p>
                  </div>
                  <p className="landing-text-body-sm text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
                    {persona.description}
                  </p>
                  <p className="mt-auto text-sm font-bold uppercase tracking-[0.04em] text-[var(--color-landing-accent)]">
                    Saves {persona.timeSaved}
                  </p>
                </StaggerItem>
              )
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* Compliance requirements stated for this industry */}
      {industry.complianceRequirements.length > 0 && (
        <section className="landing-section-sm border-t border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]">
          <div className="landing-container mb-10">
            <TextReveal as="h2" className="landing-text-display-lg text-[var(--color-landing-gray-900)] dark:text-white">
              The rules {industry.shortName.toLowerCase()} documents answer to
            </TextReveal>
          </div>
          <BadgeMarquee items={industry.complianceRequirements} label={`${industry.name} compliance requirements`} />
        </section>
      )}

      <CTASection
        variant="accent"
        align="left"
        title={industry.ctaHeadline}
        description={industry.ctaDescription}
        buttons={[
          { label: 'Book a demo', href: '/contact', variant: 'primary' },
          { label: 'Start free trial', href: '/register', variant: 'outline' },
        ]}
      />
    </>
  )
}
